export interface CollectibleStock {
  symbol: string;
  companyName: string;
  logoPath: string;
  category: string;
}

interface CollectibleEvent {
  id: string;
  label: string;
  dates: string[];
  stocks: Record<string, CollectibleStock>;
}

const COLLECTIBLE_EVENTS: CollectibleEvent[] = [
  {
    id: 'april-fools-2026',
    label: "April Fools' 2026",
    dates: ['2026-04-01'],
    stocks: {
      ENE: { symbol: 'ENE', companyName: 'Enron Corp.', logoPath: '20260401/enron.png', category: 'popular' },
      IPET: { symbol: 'IPET', companyName: 'Pets.com Inc.', logoPath: '20260401/pets-com.png', category: 'tech' },
      LEH: { symbol: 'LEH', companyName: 'Lehman Brothers', logoPath: '20260401/lehman-brothers.png', category: 'financial' },
      AERT: { symbol: 'AERT', companyName: 'Aerotyne International', logoPath: '20260401/aerotyne.png', category: 'wallstreetbets' },
      BBI: { symbol: 'BBI', companyName: 'Blockbuster Inc.', logoPath: '20260401/blockbuster.png', category: 'consumer' },
      THRNS: { symbol: 'THRNS', companyName: 'Theranos', logoPath: '20260401/theranos.png', category: 'pharma' },
      CL: { symbol: 'CL', companyName: 'Crude Oil', logoPath: '20260401/crude-oil.png', category: 'energy' },
      NKLA: { symbol: 'NKLA', companyName: 'Nikola Corp.', logoPath: '20260401/nikola.png', category: 'industrial' },
      '3333.HK': { symbol: '3333.HK', companyName: 'China Evergrande Group', logoPath: '20260401/evergrande.png', category: 'foreign' },
    },
  },
];

/** Map from "date|symbol" to CollectibleStock for fast runtime lookups */
const COLLECTIBLE_LOOKUP = new Map<string, CollectibleStock>();
for (const event of COLLECTIBLE_EVENTS) {
  for (const date of event.dates) {
    for (const [symbol, stock] of Object.entries(event.stocks)) {
      COLLECTIBLE_LOOKUP.set(`${date}|${symbol}`, stock);
    }
  }
}

/**
 * Get all collectibles for a given game date, keyed by category slug.
 * Returns undefined if no collectible event exists for that date.
 */
export function getCollectiblesForDate(gameDate: string): Map<string, CollectibleStock> | undefined {
  const event = COLLECTIBLE_EVENTS.find((e) => e.dates.includes(gameDate));
  if (!event) return undefined;
  const map = new Map<string, CollectibleStock>();
  for (const stock of Object.values(event.stocks)) {
    map.set(stock.category, stock);
  }
  return map;
}

/**
 * Get the event label for a collectible event on a given date.
 * Returns undefined if no event exists.
 */
export function getCollectibleEventLabel(gameDate: string): string | undefined {
  return COLLECTIBLE_EVENTS.find((e) => e.dates.includes(gameDate))?.label;
}

/**
 * Check if a stock won on a specific date is a collectible.
 */
export function getCollectible(symbol: string, gameDate: string): CollectibleStock | undefined {
  return COLLECTIBLE_LOOKUP.get(`${gameDate}|${symbol}`);
}

/**
 * Get the full image URL for a collectible logo.
 */
export function getCollectibleLogoUrl(collectible: CollectibleStock): string {
  return `/assets/collectibles/${collectible.logoPath}`;
}
